import { useNavigate, useParams } from 'react-router-dom'
import Markdown from 'markdown-to-jsx'
import { Helmet } from 'react-helmet-async'

import { ExternalLinkIcon } from 'ui/ExternalLinkIcon'
import { assert } from 'utils/assert'
import { Skeleton } from 'shadcdn/ui/skeleton'
import { SoftwarePageSidebar } from 'features/software/SoftwarePageSidebar'
import { useCategory, useParentCategories } from 'features/categories/hooks'
import type { SoftwareSchema } from 'openapi'
import { SoftwareRoutePath } from 'router'
import { cn } from 'utils/cn'

import { useSoftwareQuery } from './hooks'
import { SoftwareScreenshots } from './SoftwareScreenshots'

type Props = {
  className?: string
  softwareId?: number
  categorySlug?: string
  softwareSlug?: string
  vendorSlug?: string
}

export const SoftwarePage = (props: Props) => {
  const params = useParams()

  const softwareId = (props.softwareId ?? params.softwareId) ? Number(params.softwareId) : undefined
  assert(softwareId, 'No Sof id or categoryIdprovided')

  const { data: software, isLoading: softwareLoading } = useSoftwareQuery(softwareId)
  const parentCategories = useParentCategories(software?.category_id)

  const categoryName = parentCategories[parentCategories.length - 1]?.name ?? ''

  useRedirectToFullUrl(software)

  if (!softwareId) return <div>No VST id provided</div>
  if (softwareLoading || !software) return <SoftwarePageSkeleton />

  return (
    <>
      <Helmet>
        <title>
          Presync - {software.name} by {software.vendor} {'//'} {categoryName}
        </title>
      </Helmet>
      <div className="mb-10">
        {/*<div className="mb-4">*/}
        {/*  <SoftwarePageCrumbs software={software} />*/}
        {/*</div>*/}
        <SoftwareScreenshots software={software} className="mb-8" />
        <div className="flex flex-grow flex-row gap-5">
          <SoftwarePageInfo software={software} className="flex-grow" />
          <div className="h-full items-start flex-shrink-0 w-[15rem]">
            <SoftwarePageSidebar software={software} className="h-full rounded" />
          </div>
        </div>

        {/*<h2 className="mb-2 mt-6 text-xl">Packs</h2>*/}
        {/*<PacksList packs={packs!} />*/}
      </div>
    </>
  )
}

const useRedirectToFullUrl = (software: SoftwareSchema | undefined) => {
  const navigate = useNavigate()
  const params = useParams()

  const [category] = useCategory(software?.category_id ?? undefined)

  if ((!params.categorySlug || !params.softwareSlug || !params.vendorSlug) && category && software) {
    navigate(
      SoftwareRoutePath({
        softwareId: software.id,
        categorySlug: category.slug,
        vendorSlug: software.vendorSlug,
        softwareSlug: software.slug,
      }),
    )
  }
}

const SoftwarePageSkeleton = () => (
  <div className="w-full">
    <Skeleton className="w-full h-6 mb-2" />
    <div className="flex flex-col h-full backdrop-blur-md bg-white/95  shadow w-full">
      <Skeleton className="w-full h-72" />
      <div className="p-10">
        <div className="flex flex-row gap-x-10">
          <div className="w-2/3">
            <Skeleton className="w-full h-12 mb-4" />
            <Skeleton className="w-full h-72 mb-4" />
          </div>
          <div className="w-1/3">
            <Skeleton className="w-full h-12 mb-4" />
            <Skeleton className="w-full h-12 mb-4" />
          </div>
        </div>
      </div>
    </div>
  </div>
)

const SoftwarePageInfo = ({ software, className }: { software: SoftwareSchema; className?: string }) => {
  const description = software?.description?.replace('## Description', '')
  return (
    <div className={cn('flex flex-col gap-5', className)}>
      <div>
        <h1 className="text-3xl text-black font-light tracking-tighter mb-1">{software.name}</h1>

        <div className="">
          {software.url && (
            <a href={software.url} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline text-xs">
              {software.url}
              <ExternalLinkIcon />
            </a>
          )}
        </div>
      </div>

      <div className="markdown">
        <Markdown children={description ?? ''} options={{ forceBlock: true }} />
      </div>
    </div>
  )
}
